// src/admin/StudentPromotionManager.js
import React, { useState, useEffect, useMemo } from 'react';
import { firestore } from '../firebaseConfig';
import {
  collection,
  getDocs,
  doc,
  writeBatch
} from 'firebase/firestore';
import './StudentPromotionManager.css';

const StudentPromotionManager = () => {
  const [years, setYears] = useState([]);
  const [sourceYearId, setSourceYearId] = useState('2025-2026');
  const [targetYearId, setTargetYearId] = useState('2026-2027');

  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // State chứa bản xem trước
  const [previewData, setPreviewData] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    promoted: 0,
    retained: 0,
    graduated: 0
  });

  // State UI
  const [expandedClasses, setExpandedClasses] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClassFilter, setSelectedClassFilter] = useState('ALL');
  const [terminalLog, setTerminalLog] = useState('');

  useEffect(() => {
    fetchYears();
  }, []);

  const fetchYears = async () => {
    try {
      const snap = await getDocs(collection(firestore, 'academic_years'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setYears(list);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleClassExpand = (classId) => {
    setExpandedClasses(prev => ({
      ...prev,
      [classId]: !prev[classId]
    }));
  };

  const toggleAllClasses = (expand) => {
    if (!previewData) return;
    const newState = {};
    Object.keys(previewData.classesDetailMap).forEach(cId => {
      newState[cId] = expand;
    });
    setExpandedClasses(newState);
  };

  // ====================================================================
  // THUẬT TOÁN CHUYỂN LỚP VÀ SẮP XẾP THEO THỨ TỰ CẤP LỚP (SORT ORDER)
  // ====================================================================
  const handleGeneratePreview = async () => {
    if (!sourceYearId || !targetYearId) {
      alert('Vui lòng chọn đầy đủ Niên khóa nguồn và Niên khóa đích.');
      return;
    }
    if (sourceYearId === targetYearId) {
      alert('Niên khóa đích phải khác Niên khóa nguồn.');
      return;
    }

    setLoading(true);
    setPreviewData(null);
    setExpandedClasses({});
    setTerminalLog(`🔍 Khởi động thuật toán chuyển lớp theo thứ tự Cấp Lớp (${sourceYearId} ➔ ${targetYearId})...\n`);

    try {
      // 1. Tải Hồ sơ Thiếu nhi gốc (Lấy Tên Thánh, Họ Tên, Giới tính)
      const studentsSnap = await getDocs(collection(firestore, 'students'));
      const studentsMap = {};
      studentsSnap.docs.forEach(d => { studentsMap[d.id] = d.data(); });

      // 2. Tải Cấp Lớp & Lớp học NIÊN KHÓA CŨ (Nguồn)
      const sourceGradesSnap = await getDocs(collection(firestore, `academic_years/${sourceYearId}/grades`));
      const sourceClassesSnap = await getDocs(collection(firestore, `academic_years/${sourceYearId}/classes`));

      const sourceGrades = sourceGradesSnap.docs.map(d => d.data());
      const sourceClasses = sourceClassesSnap.docs.map(d => d.data());

      const sourceGradeById = {};
      sourceGrades.forEach(g => { sourceGradeById[g.id] = g; });

      const sourceClassById = {};
      sourceClasses.forEach(c => { sourceClassById[c.id] = c; });

      // 3. Tải Cấp Lớp & Lớp học NIÊN KHÓA MỚI (Đích)
      const targetGradesSnap = await getDocs(collection(firestore, `academic_years/${targetYearId}/grades`));
      const targetClassesSnap = await getDocs(collection(firestore, `academic_years/${targetYearId}/classes`));

      if (targetGradesSnap.empty) {
        throw new Error(`Niên khóa đích ${targetYearId} chưa có Cấp Lớp nào! Hãy tạo Cấp lớp trước.`);
      }
      if (targetClassesSnap.empty) {
        throw new Error(`Niên khóa đích ${targetYearId} chưa có Lớp học nào! Hãy tạo Danh sách Lớp trước.`);
      }

      // Sắp xếp Cấp Lớp đích theo sortOrder tăng dần
      const targetGrades = targetGradesSnap.docs.map(d => d.data()).sort((a,b) => (a.sortOrder||0) - (b.sortOrder||0));
      
      // Bảng tra cứu sortOrder của Cấp Lớp đích
      const targetGradeOrderMap = {};
      const targetGradeBySortOrder = {};
      targetGrades.forEach(g => {
        targetGradeOrderMap[g.id] = g.sortOrder || 0;
        targetGradeBySortOrder[g.sortOrder] = g;
      });

      const maxTargetSortOrder = Math.max(...targetGrades.map(g => g.sortOrder || 0));

      // SẮP XẾP DANH SÁCH LỚP NĂM MỚI THEO CẤP LỚP (TẦNG 1) & THỨ TỰ LỚP (TẦNG 2)
      const targetClasses = targetClassesSnap.docs.map(d => d.data());
      targetClasses.sort((a, b) => {
        const gradeOrderA = targetGradeOrderMap[a.gradeId] ?? 999;
        const gradeOrderB = targetGradeOrderMap[b.gradeId] ?? 999;
        if (gradeOrderA !== gradeOrderB) {
          return gradeOrderA - gradeOrderB; // Sắp xếp theo Cấp Lớp trước
        }
        return (a.sortOrder || 0) - (b.sortOrder || 0); // Nếu cùng cấp lớp, xếp theo Lớp
      });

      // 4. Tải Enrollments Niên khóa Cũ
      const enrollmentsSnap = await getDocs(collection(firestore, `academic_years/${sourceYearId}/enrollments`));
      if (enrollmentsSnap.empty) {
        throw new Error(`Không tìm thấy dữ liệu thiếu nhi trong Niên khóa nguồn ${sourceYearId}.`);
      }

      setTerminalLog(prev => prev + `✓ Tải thành công ${enrollmentsSnap.size} thiếu nhi thuộc niên khóa ${sourceYearId}.\n`);

      // Hàm tìm sortOrder cũ chuẩn xác nhất cho từng học sinh
      const findSourceSortOrder = (enr) => {
        if (enr.gradeId && sourceGradeById[enr.gradeId]) {
          return sourceGradeById[enr.gradeId].sortOrder;
        }
        if (enr.classId && sourceClassById[enr.classId]) {
          const gId = sourceClassById[enr.classId].gradeId;
          if (gId && sourceGradeById[gId]) {
            return sourceGradeById[gId].sortOrder;
          }
        }
        const rawKey = (enr.gradeId || enr.classId || '').toLowerCase();
        const matched = sourceGrades.find(g => rawKey.startsWith(g.id) || g.id.startsWith(rawKey));
        if (matched) return matched.sortOrder;

        return null;
      };

      // Mảng gom nhóm theo Cấp lớp ĐÍCH
      const targetGradeBuckets = {}; 
      targetGrades.forEach(g => { targetGradeBuckets[g.id] = []; });

      let countPromoted = 0;
      let countRetained = 0;
      let countGraduated = 0;
      const graduatedStudentsList = [];

      // 5. Lặp qua toàn bộ thiếu nhi và tính toán định tuyến
      enrollmentsSnap.docs.forEach(docSnap => {
        const enr = docSnap.data();
        const sId = enr.studentId;
        const profile = studentsMap[sId] || {};
        const gender = profile.gender || 'chua_xac_dinh';

        const yearlyRank = enr.transcript?.yearly?.rank || '';
        const isPromoted = yearlyRank === 'Được lên lớp' || yearlyRank === "Hạng 1" || yearlyRank === "Hạng 2" || yearlyRank === "Hạng 3"; // Xác định dựa trên xếp loại hàng năm

        const oldSortOrder = findSourceSortOrder(enr);
        if (oldSortOrder === null) return;

        let targetSortOrder = oldSortOrder;
        if (isPromoted) {
          countPromoted++;
          targetSortOrder = oldSortOrder + 1; // Tăng lên 1 cấp
        } else {
          countRetained++;
          targetSortOrder = oldSortOrder; // Giữ nguyên cấp cũ
        }

        // Tốt nghiệp nếu vượt quá cấp cao nhất
        if (isPromoted && targetSortOrder > maxTargetSortOrder) {
          countGraduated++;
          graduatedStudentsList.push({
            studentId: sId,
            holyName: profile.holyName || '',
            fullName: profile.fullName || sId,
            gender: gender,
            oldClassId: enr.classId || 'N/A',
            status: 'Tốt nghiệp'
          });
          return;
        }

        let targetGradeObj = targetGradeBySortOrder[targetSortOrder];
        if (!targetGradeObj) {
          targetGradeObj = targetGrades.find(g => g.sortOrder >= targetSortOrder) || targetGrades[targetGrades.length - 1];
        }

        if (targetGradeObj) {
          if (!targetGradeBuckets[targetGradeObj.id]) targetGradeBuckets[targetGradeObj.id] = [];
          targetGradeBuckets[targetGradeObj.id].push({
            studentId: sId,
            holyName: profile.holyName || '',
            fullName: profile.fullName || sId,
            gender: gender,
            isPromoted: isPromoted,
            oldClassId: enr.classId || 'N/A',
            oldSortOrder: oldSortOrder,
            targetSortOrder: targetSortOrder
          });
        }
      });

      // 6. THUẬT TOÁN CHIA ĐỀU VÀO CÁC LỚP THEO GIỚI TÍNH
      const finalEnrollmentsToSave = [];
      const classesDetailMap = {};

      // Khởi tạo các Lớp theo ĐÚNG THỨ TỰ targetClasses đã được sort
      targetClasses.forEach(c => {
        classesDetailMap[c.id] = {
          classId: c.id,
          className: c.name,
          gradeId: c.gradeId,
          gradeSortOrder: targetGradeOrderMap[c.gradeId] || 0,
          group: c.group || '',
          maleCount: 0,
          femaleCount: 0,
          promotedCount: 0,
          retainedCount: 0,
          students: []
        };
      });

      Object.keys(targetGradeBuckets).forEach(gradeId => {
        const studentList = targetGradeBuckets[gradeId];
        const gradeClasses = targetClasses.filter(c => c.gradeId === gradeId);

        if (gradeClasses.length === 0) return;

        const maleClasses = gradeClasses.filter(c => c.id.endsWith('-nam') || c.name.toLowerCase().includes('nam'));
        const femaleClasses = gradeClasses.filter(c => c.id.endsWith('-nu') || c.name.toLowerCase().includes('nữ'));
        const mixedClasses = gradeClasses.filter(c => !maleClasses.includes(c) && !femaleClasses.includes(c));

        const maleStudents = studentList.filter(s => s.gender === 'nam');
        const femaleStudents = studentList.filter(s => s.gender === 'nu');
        const otherStudents = studentList.filter(s => s.gender !== 'nam' && s.gender !== 'nu');

        const distributeRoundRobin = (stus, targetClsList) => {
          if (targetClsList.length === 0) targetClsList = gradeClasses;
          stus.forEach((stu, idx) => {
            const assignedClass = targetClsList[idx % targetClsList.length];
            
            const studentEntry = {
              ...stu,
              newClassId: assignedClass.id,
              newClassName: assignedClass.name
            };

            finalEnrollmentsToSave.push({
              studentId: stu.studentId,
              gradeId: gradeId,
              classId: assignedClass.id,
              status: 'Đang học',
              transcript: { semester1: {}, semester2: {}, yearly: {} }
            });

            if (classesDetailMap[assignedClass.id]) {
              const targetReport = classesDetailMap[assignedClass.id];
              targetReport.students.push(studentEntry);
              if (stu.gender === 'nam') targetReport.maleCount++;
              if (stu.gender === 'nu') targetReport.femaleCount++;
              if (stu.isPromoted) targetReport.promotedCount++;
              else targetReport.retainedCount++;
            }
          });
        };

        distributeRoundRobin(maleStudents, maleClasses.length > 0 ? maleClasses : mixedClasses);
        distributeRoundRobin(femaleStudents, femaleClasses.length > 0 ? femaleClasses : mixedClasses);
        distributeRoundRobin(otherStudents, mixedClasses.length > 0 ? mixedClasses : gradeClasses);
      });

      setStats({
        total: enrollmentsSnap.size,
        promoted: countPromoted,
        retained: countRetained,
        graduated: countGraduated
      });

      setPreviewData({
        enrollmentsToSave: finalEnrollmentsToSave,
        graduatedList: graduatedStudentsList,
        classesDetailMap: classesDetailMap
      });

      const firstClassId = Object.keys(classesDetailMap)[0];
      if (firstClassId) setExpandedClasses({ [firstClassId]: true });

      setTerminalLog(prev => prev + `🎉 HOÀN TẤT TÍNH TOÁN! Các lớp đã được sắp xếp chuẩn xác theo Thứ Tự Cấp Lớp (sortOrder).\n`);

    } catch (err) {
      console.error(err);
      setTerminalLog(prev => prev + `❌ LỖI KHỞI TẠO: ${err.message}\n`);
    } finally {
      setLoading(false);
    }
  };

  // Lọc dữ liệu hiển thị bản xem trước
  const filteredClassEntries = useMemo(() => {
    if (!previewData) return [];
    
    let entries = Object.entries(previewData.classesDetailMap);

    // Luôn bảo đảm thứ tự theo gradeSortOrder
    entries.sort((a, b) => (a[1].gradeSortOrder || 0) - (b[1].gradeSortOrder || 0));

    if (selectedClassFilter !== 'ALL') {
      entries = entries.filter(([cId]) => cId === selectedClassFilter);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      entries = entries.map(([cId, classInfo]) => {
        const matchingStudents = classInfo.students.filter(s => 
          s.fullName.toLowerCase().includes(term) ||
          s.holyName.toLowerCase().includes(term) ||
          s.studentId.toLowerCase().includes(term) ||
          s.oldClassId.toLowerCase().includes(term)
        );
        return [cId, { ...classInfo, students: matchingStudents }];
      }).filter(([_, classInfo]) => classInfo.students.length > 0);
    }

    return entries;
  }, [previewData, searchTerm, selectedClassFilter]);

  // ====================================================================
  // XÁC NHẬN LƯU VÀO FIRESTORE
  // ====================================================================
  const handleCommitPromotion = async () => {
    if (!previewData || previewData.enrollmentsToSave.length === 0) {
      alert('Chưa có dữ liệu bản xem trước.');
      return;
    }

    if (!window.confirm(`XÁC NHẬN: Lưu phân lớp Niên khóa ${targetYearId} cho ${previewData.enrollmentsToSave.length} thiếu nhi?`)) {
      return;
    }

    setProcessing(true);
    setTerminalLog(prev => prev + `🚀 Đang lưu phân lớp mới vào Firestore (Niên khóa ${targetYearId})...\n`);

    try {
      const enrollmentsList = previewData.enrollmentsToSave;
      let batch = writeBatch(firestore);
      let count = 0;

      for (const item of enrollmentsList) {
        const docRef = doc(firestore, `academic_years/${targetYearId}/enrollments`, item.studentId);
        batch.set(docRef, item);

        count++;
        if (count % 400 === 0) {
          await batch.commit();
          batch = writeBatch(firestore);
          setTerminalLog(prev => prev + `  + Đã lưu ${count}/${enrollmentsList.length} tài liệu...\n`);
        }
      }

      if (count % 400 !== 0) {
        await batch.commit();
      }

      setTerminalLog(prev => prev + `✅ THÀNH CÔNG! Toàn bộ thiếu nhi đã được chuyển sang Niên khóa ${targetYearId}.\n`);
      alert(`Đã lưu thành công danh sách chuyển lớp cho Niên khóa ${targetYearId}!`);

    } catch (err) {
      console.error(err);
      setTerminalLog(prev => prev + `❌ LỖI LƯU THẤT BẠI: ${err.message}\n`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="student-promotion-manager">
      <div className="spm-card">
        <h2 className="spm-title">Chuyển Lớp & Xem Trước Chi Tiết Theo Thứ Tự Cấp Lớp</h2>
        <p className="spm-subtitle">
          Thuật toán tự động sắp xếp các lớp dựa theo <strong>thứ tự Cấp Lớp (sortOrder)</strong> từ nhỏ đến lớn. Đẩy thiếu nhi <strong>"Được lên lớp"</strong> lên cấp trên và giữ nguyên cấp cũ với thiếu nhi <strong>"Ở lại lớp"</strong>.
        </p>

        {/* Thanh chọn Niên Khóa */}
        <div className="spm-selection-row">
          <div className="spm-select-group">
            <label className="spm-label">Niên Khóa Nguồn (Cũ):</label>
            <select 
              value={sourceYearId} 
              onChange={e => setSourceYearId(e.target.value)}
              className="spm-select"
            >
              {years.map(y => (
                <option key={y.id} value={y.id}>{y.name} ({y.id})</option>
              ))}
            </select>
          </div>

          <div className="spm-arrow-icon">➔</div>

          <div className="spm-select-group">
            <label className="spm-label">Niên Khóa Đích (Mới):</label>
            <select 
              value={targetYearId} 
              onChange={e => setTargetYearId(e.target.value)}
              className="spm-select"
            >
              {years.map(y => (
                <option key={y.id} value={y.id}>{y.name} ({y.id})</option>
              ))}
            </select>
          </div>

          <button 
            onClick={handleGeneratePreview}
            disabled={loading || processing}
            className="spm-btn spm-btn-preview"
          >
            {loading ? '🔍 Đang phân tích...' : '👁️ Tạo Bản Xem Trước Chi Tiết'}
          </button>
        </div>

        {/* Log Console */}
        {terminalLog && (
          <textarea readOnly value={terminalLog} className="spm-terminal" />
        )}

        {/* BẢN XEM TRƯỚC CHI TIẾT */}
        {previewData && (
          <div className="spm-preview-section">
            <h3 className="spm-section-title">📊 Bảng Thống Kê Chuyển Lớp (Sắp Xếp Theo Cấp Lớp)</h3>
            
            <div className="spm-stats-grid">
              <div className="spm-stat-card">
                <span className="spm-stat-number">{stats.total}</span>
                <span className="spm-stat-label">Tổng Thiếu Nhi</span>
              </div>
              <div className="spm-stat-card spm-stat-green">
                <span className="spm-stat-number">{stats.promoted}</span>
                <span className="spm-stat-label">Được Lên Lớp</span>
              </div>
              <div className="spm-stat-card spm-stat-orange">
                <span className="spm-stat-number">{stats.retained}</span>
                <span className="spm-stat-label">Ở Lại Lớp Cũ</span>
              </div>
              <div className="spm-stat-card spm-stat-purple">
                <span className="spm-stat-number">{stats.graduated}</span>
                <span className="spm-stat-label">Tốt Nghiệp</span>
              </div>
            </div>

            {/* Thanh Công Cụ Lọc */}
            <div className="spm-filter-bar">
              <div className="spm-search-box">
                <input 
                  type="text" 
                  placeholder="🔎 Tìm theo tên thiếu nhi, tên thánh, mã TN, lớp cũ..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="spm-filter-input"
                />
              </div>

              <select 
                value={selectedClassFilter} 
                onChange={e => setSelectedClassFilter(e.target.value)}
                className="spm-filter-select"
              >
                <option value="ALL">-- Tất cả các lớp mới ({Object.keys(previewData.classesDetailMap).length} lớp) --</option>
                {filteredClassEntries.map(([cId, cInfo]) => (
                  <option key={cId} value={cId}>{cInfo.className} ({cInfo.students.length} em)</option>
                ))}
              </select>

              <div className="spm-toggle-buttons">
                <button onClick={() => toggleAllClasses(true)} className="spm-btn-sm">Mở tất cả</button>
                <button onClick={() => toggleAllClasses(false)} className="spm-btn-sm">Thu gọn</button>
              </div>
            </div>

            {/* ACCORDION TỪNG LỚP HỌC MỚI (ĐÃ SẮP XẾP THEO CẤP LỚP) */}
            <div className="spm-classes-accordion-container">
              {filteredClassEntries.map(([classId, classInfo]) => {
                const isExpanded = expandedClasses[classId];
                return (
                  <div key={classId} className="spm-accordion-card">
                    <div 
                      className="spm-accordion-header"
                      onClick={() => toggleClassExpand(classId)}
                    >
                      <div className="spm-accordion-title-group">
                        <span className="spm-accordion-arrow">{isExpanded ? '▼' : '▶'}</span>
                        <div>
                          <h4 className="spm-class-title">{classInfo.className}</h4>
                          <span className="spm-class-code">Thứ tự cấp: {classInfo.gradeSortOrder} | Mã Lớp: {classId}</span>
                        </div>
                      </div>

                      <div className="spm-accordion-badges">
                        <span className="spm-badge spm-badge-total">Sĩ số: {classInfo.students.length}</span>
                        <span className="spm-badge spm-badge-gender">Nam: {classInfo.maleCount} | Nữ: {classInfo.femaleCount}</span>
                        <span className="spm-badge spm-badge-promoted">Từ cấp dưới lên: {classInfo.promotedCount}</span>
                        {classInfo.retainedCount > 0 && (
                          <span className="spm-badge spm-badge-retained">Ở lại lớp cũ: {classInfo.retainedCount}</span>
                        )}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="spm-accordion-body">
                        {classInfo.students.length === 0 ? (
                          <div className="spm-empty-text">Chưa có thiếu nhi nào được phân vào lớp này.</div>
                        ) : (
                          <div className="spm-table-wrapper">
                            <table className="spm-detail-table">
                              <thead>
                                <tr>
                                  <th>STT</th>
                                  <th>Mã TN</th>
                                  <th>Tên Thánh</th>
                                  <th>Họ và Tên</th>
                                  <th>Giới tính</th>
                                  <th>Lớp Cũ ({sourceYearId})</th>
                                  <th>Chuyển Lớp Theo sortOrder</th>
                                </tr>
                              </thead>
                              <tbody>
                                {classInfo.students.map((stu, index) => (
                                  <tr key={stu.studentId} className={stu.isPromoted ? '' : 'spm-row-retained'}>
                                    <td className="spm-text-center">{index + 1}</td>
                                    <td className="spm-code-text">{stu.studentId}</td>
                                    <td>{stu.holyName || '-'}</td>
                                    <td className="spm-font-bold">{stu.fullName}</td>
                                    <td className="spm-text-center">
                                      <span className={`spm-gender-tag ${stu.gender}`}>
                                        {stu.gender === 'nam' ? 'Nam' : stu.gender === 'nu' ? 'Nữ' : 'N/A'}
                                      </span>
                                    </td>
                                    <td className="spm-code-text">{stu.oldClassId}</td>
                                    <td>
                                      {stu.isPromoted ? (
                                        <span className="spm-status-tag spm-tag-promoted">
                                          ▲ Tăng cấp (order {stu.oldSortOrder} ➔ {stu.targetSortOrder})
                                        </span>
                                      ) : (
                                        <span className="spm-status-tag spm-tag-retained">
                                          🔴 Giữ cấp cũ (order {stu.oldSortOrder})
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* DANH SÁCH TỐT NGHIỆP */}
            {previewData.graduatedList.length > 0 && (
              <div className="spm-graduated-section">
                <h4 className="spm-graduated-title">🎓 Danh Sách Thiếu Nhi Tốt Nghiệp ({previewData.graduatedList.length} em)</h4>
                <div className="spm-table-wrapper">
                  <table className="spm-detail-table">
                    <thead>
                      <tr>
                        <th>STT</th>
                        <th>Mã TN</th>
                        <th>Tên Thánh</th>
                        <th>Họ và Tên</th>
                        <th>Giới Tính</th>
                        <th>Lớp Cũ</th>
                        <th>Trạng Thái</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.graduatedList.map((stu, idx) => (
                        <tr key={stu.studentId}>
                          <td className="spm-text-center">{idx + 1}</td>
                          <td className="spm-code-text">{stu.studentId}</td>
                          <td>{stu.holyName || '-'}</td>
                          <td className="spm-font-bold">{stu.fullName}</td>
                          <td className="spm-text-center">{stu.gender === 'nam' ? 'Nam' : 'Nữ'}</td>
                          <td className="spm-code-text">{stu.oldClassId}</td>
                          <td><span className="spm-status-tag spm-tag-graduated">🎓 Tốt nghiệp</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="spm-commit-bar">
              <button 
                onClick={handleCommitPromotion}
                disabled={processing}
                className="spm-btn spm-btn-commit"
              >
                {processing ? '⏳ Đang lưu Firestore...' : `💾 Xác Nhận Lưu Phân Lớp Cho Niên Khóa ${targetYearId}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentPromotionManager;